/**
 * base.ts
 * Core cache state, listeners, and stale-check logic.
 */

import type { Product } from '../products';
import type { PortfolioProject } from '../portfolio-data';
import type { PublicNewsItem, PublicWTBPoint } from '../dataSource';
import type { HomePageData } from '../homeContent';

export interface CacheEntry<T> {
  data: T;
  fetchedAt: number;
}

export const STALE_TIME_MS = 5 * 60 * 1000;

export const cache = {
  products: null as CacheEntry<Product[]> | null,
  portfolio: null as CacheEntry<PortfolioProject[]> | null,
  news: null as CacheEntry<PublicNewsItem[]> | null,
  whereToBuy: null as CacheEntry<PublicWTBPoint[]> | null,
  homeData: new Map<string, CacheEntry<HomePageData>>(),
  esgData: new Map<string, CacheEntry<any>>(), // Typed in esg.ts
  heroTexts: new Map<string, CacheEntry<{ eyebrow: string; title: string; subtitle: string }>>(),
  heroMedia: null as CacheEntry<any[]> | null,
  homeCta: new Map<string, CacheEntry<{ cta1Text: string; cta1Url: string; cta2Text: string; cta2Url: string }>>(),
};

export const inFlight = {
  products: null as Promise<Product[]> | null,
  portfolio: null as Promise<PortfolioProject[]> | null,
  news: null as Promise<PublicNewsItem[]> | null,
  whereToBuy: null as Promise<PublicWTBPoint[]> | null,
};

export const homeInFlight = new Map<string, Promise<HomePageData>>();
export const esgInFlight = new Map<string, Promise<any>>();
export const heroInFlight = new Map<string, Promise<{ eyebrow: string; title: string; subtitle: string }>>();

type Listener = () => void;
const listeners = new Set<Listener>();

export function subscribeToCache(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function notify() {
  for (const fn of listeners) {
    try { fn(); } catch { /* ignore */ }
  }
}

export function isStale<T>(entry: CacheEntry<T> | null): boolean {
  if (!entry) return true;
  return Date.now() - entry.fetchedAt > STALE_TIME_MS;
}

export const ALL_PAGE_KEYS = ['home', 'catalog', 'portfolio', 'news', 'about', 'wheretobuy', 'becomedealer', 'useful', 'esg'];
export const ALL_LANGS = ['ru', 'en', 'uz'];
